import React, { useState, useEffect } from 'react'
import { Search, Star, ExternalLink } from 'lucide-react'
import { productsAPI } from '../services/api'
import toast from 'react-hot-toast'

interface Product {
  id: string
  name: string
  description?: string
  amazonId?: string
  amazonUrl?: string
  imageUrl?: string
  category?: string
  alternativeRoutes: AlternativeRoute[]
  reviews: Review[]
  stats: {
    alternativeRoutes: number
    reviews: number
    coupons: number
  }
}

interface AlternativeRoute {
  id: string
  websiteName: string
  websiteUrl: string
  price?: number
  currency: string
  status: string
  couponsCount: number
}

interface Review {
  id: string
  rating: number
  comment?: string
  userName?: string
  createdAt: string
}

export default function Home() {
  const [searchQuery, setSearchQuery] = useState('')
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(false)
  const [popularProducts, setPopularProducts] = useState<Product[]>([])

  useEffect(() => {
    loadPopularProducts()
  }, [])

  const loadPopularProducts = async () => {
    try {
      const response = await productsAPI.getPopular(6)
      setPopularProducts(response.products || [])
    } catch (error) {
      console.error('Failed to load popular products:', error)
      setPopularProducts([])
    }
  }

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!searchQuery.trim()) return

    setLoading(true)
    try {
      const response = await productsAPI.search(searchQuery)
      setProducts(response.products || [])
      if (!response.products || response.products.length === 0) {
        toast.info('No products found. Try a different search term.')
      }
    } catch (error) {
      toast.error('Search failed. Please try again.')
      console.error('Search error:', error)
      setProducts([])
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4">
              Monobreak
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Find Amazon alternatives and save money by buying directly from sellers
            </p>
            
            {/* Search Form */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for products, Amazon URLs, or product IDs..."
                  className="w-full px-6 py-4 text-lg border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm pr-12"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="absolute right-2 top-2 bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-full transition-colors disabled:opacity-50"
                >
                  <Search className="w-5 h-5" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Results */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Searching for products...</p>
          </div>
        )}

        {!loading && products && products.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Search Results for "{searchQuery}"
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        )}

        {/* Popular Products */}
        {!loading && (!products || products.length === 0) && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Popular Products
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {popularProducts && popularProducts.length > 0 ? (
                popularProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))
              ) : (
                <div className="col-span-full text-center py-12 text-gray-500">
                  No popular products available at the moment.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function ProductCard({ product }: { product: Product }) {
  const formatPrice = (price?: number, currency = 'USD') => {
    if (!price) return 'Price not available'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(price)
  }

  const getAverageRating = (reviews: Review[]) => {
    if (!reviews || reviews.length === 0) return 0
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0)
    return Math.round((sum / reviews.length) * 10) / 10
  }

  // Ensure all arrays exist before accessing them
  const alternativeRoutes = product.alternativeRoutes || []
  const reviews = product.reviews || []
  const stats = product.stats || { alternativeRoutes: 0, reviews: 0, coupons: 0 }

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow border">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {product.name}
            </h3>
            {product.description && (
              <p className="text-gray-600 text-sm mb-3">
                {product.description}
              </p>
            )}
          </div>
          {product.imageUrl && (
            <img
              src={product.imageUrl}
              alt={product.name}
              className="w-16 h-16 object-cover rounded ml-4"
            />
          )}
        </div>

        {/* Rating */}
        {reviews.length > 0 && (
          <div className="flex items-center mb-3">
            <div className="flex items-center">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-4 h-4 ${
                    i < Math.floor(getAverageRating(reviews))
                      ? 'text-yellow-400 fill-current'
                      : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
            <span className="text-sm text-gray-600 ml-2">
              {getAverageRating(reviews)} ({reviews.length} reviews)
            </span>
          </div>
        )}

        {/* Alternative Routes */}
        <div className="space-y-2 mb-4">
          <h4 className="text-sm font-medium text-gray-900">
            Alternative Sources ({stats.alternativeRoutes})
          </h4>
          {alternativeRoutes.length > 0 ? (
            alternativeRoutes.slice(0, 3).map((route) => (
              <div key={route.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{route.websiteName}</p>
                  {route.price && (
                    <p className="text-sm text-gray-600">
                      {formatPrice(route.price, route.currency)}
                    </p>
                  )}
                </div>
                <a
                  href={route.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                >
                  Visit <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </div>
            ))
          ) : (
            <div className="text-sm text-gray-500 p-2">No alternatives found</div>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>{stats.alternativeRoutes} alternatives</span>
          <span>{stats.coupons} coupons</span>
          <span>{stats.reviews} reviews</span>
        </div>
      </div>
    </div>
  )
}
